import { DependencyList, useMemo } from "react";
import useChangingCallback from "./use-changing-callback";

export type DynamicParameter<T> = [T] extends [never] ? [] : [T];

export function debounceWithManager<T = never>(handler: (...args: DynamicParameter<T>) => void, delay: number) {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const bounce = (...args: DynamicParameter<T>) => {
        if (timer) {
            clearTimeout(timer);
        }
        timer = setTimeout(() => handler(...args), delay);
    };

    return {
        bounce,
        reset: () => {
            clearTimeout(timer);
        },
        fireNow: (...args: DynamicParameter<T>) => {
            clearTimeout(timer);
            handler(...args);
        },
    };
}

export function debounce<T = never>(handler: (...args: DynamicParameter<T>) => void, delay: number) {
    let timer: ReturnType<typeof setTimeout> | undefined;
    return (...args: DynamicParameter<T>) => {
        if (timer) {
            clearTimeout(timer);
        }
        timer = setTimeout(() => handler(...args), delay);
    };
}

export function createDebounce(delay: number) {
    let timer: ReturnType<typeof setTimeout> | undefined;
    return (handler: () => void) => {
        if (timer) {
            clearTimeout(timer);
        }
        timer = setTimeout(handler, delay);
    };
}

export function createDebounceWithHandler<T extends unknown[]>(delay: number, handler: (...args: T) => void) {
    let timer: ReturnType<typeof setTimeout> | undefined;
    return (...args: T) => {
        if (timer) {
            clearTimeout(timer);
        }
        timer = setTimeout(() => handler(...args), delay);
    };
}

export function useDebounce<T = never>(
    handler: (...args: DynamicParameter<T>) => void,
    delay: number,
    dependencies?: DependencyList,
) {
    const wrappedHandler = useChangingCallback(handler, dependencies ?? []);
    return useMemo(() => debounce(wrappedHandler, delay), dependencies ?? []);
}

export function useChangingDebounce<T = never>(
    handler: (...args: DynamicParameter<T>) => void,
    delay: number,
    dependencies?: DependencyList,
) {
    const wrappedHandler = useChangingCallback(handler, dependencies ?? []);
    return useMemo(() => debounce(wrappedHandler, delay), []);
}

export function useDebounceWithManager<T = never>(
    handler: (...args: DynamicParameter<T>) => void,
    delay: number,
    dependencies?: DependencyList,
) {
    const wrappedHandler = useChangingCallback(handler, dependencies ?? []);
    return useMemo(() => debounceWithManager(wrappedHandler, delay), dependencies ?? []);
}
